import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import { Box, Chip, ListItemButton, Stack, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { TAAS_SANS, TAAS_SERIF } from "@/shared/branding/taasBrand";
import useThemeStore from "@/shared/stores/useThemeStore";
import { formatDeckCardTypeLabel } from "@/cards/common/types/app-deck/deckCardThumbnail.types";
import {
  formatTocReleaseLabel,
  getTocExpReleaseLabel,
  stripDeckCardHtml,
} from "@/cards/common/utils/app-deck/deckCardTocDisplay";
import { findDemoCardEmbeddedExps } from "@/cards/demo-card/utils/demoCardPreviewUtils";
import { findSeasonCardEmbeddedExps } from "@/cards/season-card/utils/seasonCardPreviewUtils";

export type CardTocPageItem = {
  pageIndex: number;
  title: string;
  description?: string;
  locked?: boolean;
  releaseLabel?: string | null;
  sessionMeta?: string;
};

type CardTocDrawerProps = {
  cardTitle?: string;
  cardDescription?: string;
  pageCount: number;
  pages: CardTocPageItem[];
  currentPage: number;
  onSelectPage: (pageIndex: number) => void;
  /** When false, only the table-of-contents list is shown. Default true. */
  showOverview?: boolean;
};

export function CardTocDrawer({
  cardTitle,
  cardDescription,
  pageCount,
  pages,
  currentPage,
  onSelectPage,
  showOverview = true,
}: CardTocDrawerProps) {
  const { t } = useTranslation();
  const { darkMode } = useThemeStore();

  const titleColor = darkMode ? "var(--ink-50)" : "var(--neutral-900)";
  const bodyColor = darkMode ? "var(--neutral-400)" : "var(--neutral-600)";
  const mutedColor = darkMode ? "var(--neutral-500)" : "var(--neutral-500)";
  const accentColor = "var(--primary-600)";
  const surfaceBg = darkMode ? "var(--ink-900)" : "var(--primary-100)";
  const surfaceBorder = darkMode
    ? "1px solid color-mix(in srgb, var(--primary-600) 28%, transparent)"
    : "1px solid var(--primary-300)";
  const chipBg = darkMode
    ? "color-mix(in srgb, var(--primary-600) 16%, transparent)"
    : "var(--primary-200)";
  const chipColor = darkMode ? "var(--primary-900)" : "var(--primary-900-dark)";
  const dividerColor = darkMode
    ? "color-mix(in srgb, var(--primary-600) 16%, transparent)"
    : "var(--primary-200)";
  const leaderColor = darkMode
    ? "color-mix(in srgb, var(--primary-600) 38%, transparent)"
    : "var(--primary-300)";
  const releasedColor = darkMode ? "var(--secondary-400)" : "var(--secondary-800)";
  const upcomingColor = darkMode
    ? "color-mix(in srgb, var(--primary-600) 82%, var(--ink-50))"
    : "var(--primary-900-dark)";

  const overviewTitle = cardTitle?.trim() || t("card_toc_untitled", { defaultValue: "Card" });
  const overviewDescription =
    stripDeckCardHtml(cardDescription) ||
    t("card_toc_overview_body", {
      defaultValue: "{{count}} pages",
      count: pageCount,
    });

  return (
    <Box
      sx={{
        px: 0,
        pb: 2.5,
        maxWidth: 640,
        mx: "auto",
        width: "100%",
        flex: 1,
        minHeight: 0,

        // IMPORTANT: make this a column layout
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack
        spacing={2.25}
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {" "}
        {showOverview ? (
          <Box>
            <Typography
              sx={{
                fontSize: "0.58rem",
                letterSpacing: "0.12em",
                color: mutedColor,
                textTransform: "uppercase",
                mb: 0.85,
                fontFamily: TAAS_SANS,
              }}
            >
              {t("card_toc_overview", { defaultValue: "Overview" })}
            </Typography>
            <Box
              sx={{
                p: { xs: 1.25, sm: 1.5 },
                borderRadius: "12px",
                bgcolor: surfaceBg,
                border: surfaceBorder,
              }}
            >
              <Stack spacing={1}>
                <Chip
                  icon={<MenuBookOutlinedIcon sx={{ fontSize: "1rem !important" }} />}
                  label={t("card_toc_pages", { defaultValue: "Pages" })}
                  size="small"
                  sx={{
                    alignSelf: "flex-start",
                    bgcolor: chipBg,
                    color: chipColor,
                    border: surfaceBorder,
                    fontFamily: TAAS_SANS,
                    fontWeight: 600,
                    "& .MuiChip-icon": { color: accentColor },
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: TAAS_SERIF,
                    fontSize: "1.05rem",
                    fontWeight: 700,
                    color: titleColor,
                  }}
                >
                  {overviewTitle}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: TAAS_SANS,
                    fontSize: "0.86rem",
                    lineHeight: 1.55,
                    color: bodyColor,
                  }}
                >
                  {overviewDescription}
                </Typography>
              </Stack>
            </Box>
          </Box>
        ) : null}
        {pages.length > 0 ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* FIXED */}
            <Typography
              sx={{
                flexShrink: 0,
                fontSize: "0.58rem",
                letterSpacing: "0.12em",
                color: mutedColor,
                textTransform: "uppercase",
                mb: 0.85,
                fontFamily: TAAS_SANS,
              }}
            >
              {t("card_table_of_contents", { defaultValue: "Table of contents" })}
            </Typography>
            {/* SCROLLABLE */}
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",

                p: { xs: 0.75, sm: 1 },
                borderRadius: "12px",
                bgcolor: surfaceBg,
                border: surfaceBorder,

                scrollbarWidth: "thin",
                scrollbarColor: darkMode
                  ? "color-mix(in srgb, var(--primary-600) 35%, transparent) transparent"
                  : "var(--primary-300) transparent",
              }}
            >
              <Stack spacing={0} divider={<Box sx={{ height: 1, bgcolor: dividerColor, mx: 1 }} />}>
                {pages.map((page, index) => {
                  const isActive = currentPage === page.pageIndex;
                  const isLocked = Boolean(page.locked);
                  const released =
                    page.releaseLabel?.toLowerCase() === "released" ||
                    page.releaseLabel?.toLowerCase() ===
                      t("deck_toc_released", { defaultValue: "Released" }).toLowerCase();

                  return (
                    <ListItemButton
                      key={`${page.pageIndex}-${page.title}`}
                      disabled={isLocked}
                      selected={isActive}
                      onClick={isLocked ? undefined : () => onSelectPage(page.pageIndex)}
                      sx={{
                        borderRadius: "10px",
                        py: 1.1,
                        px: 1,
                        alignItems: "flex-start",
                        gap: 1,
                        opacity: isLocked ? 0.55 : 1,
                        "&.Mui-disabled": { opacity: 0.55 },
                        "&.Mui-selected": {
                          bgcolor: darkMode
                            ? "color-mix(in srgb, var(--primary-600) 14%, transparent)"
                            : "var(--primary-100-light)",
                        },
                        "&:hover": {
                          bgcolor: darkMode
                            ? "color-mix(in srgb, var(--primary-600) 10%, transparent)"
                            : "var(--primary-100-light)",
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          minWidth: 28,
                          pt: 0.2,
                          fontSize: "0.84rem",
                          fontWeight: 800,
                          color: accentColor,
                          fontFamily: TAAS_SANS,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {index + 1}.
                      </Typography>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "baseline",
                            gap: 0.5,
                            width: "100%",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.9rem",
                              fontWeight: 700,
                              color: titleColor,
                              fontFamily: TAAS_SANS,
                              lineHeight: 1.35,
                              flexShrink: 1,
                              minWidth: 0,
                            }}
                          >
                            {page.title}
                          </Typography>

                          {page.releaseLabel ? (
                            <>
                              <Box
                                aria-hidden
                                sx={{
                                  flex: 1,
                                  minWidth: 12,
                                  borderBottom: `1px dotted ${leaderColor}`,
                                  mx: 0.5,
                                  mb: 0.3,
                                  alignSelf: "flex-end",
                                }}
                              />
                              <Typography
                                sx={{
                                  flexShrink: 0,
                                  fontSize: "0.7rem",
                                  fontWeight: 700,
                                  letterSpacing: "0.02em",
                                  fontFamily: TAAS_SANS,
                                  color: released ? releasedColor : upcomingColor,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {page.releaseLabel}
                              </Typography>
                            </>
                          ) : null}
                        </Box>

                        {page.sessionMeta ? (
                          <Typography
                            sx={{
                              mt: 0.35,
                              fontSize: "0.68rem",
                              color: mutedColor,
                              fontFamily: TAAS_SANS,
                            }}
                          >
                            {page.sessionMeta}
                          </Typography>
                        ) : null}

                        {page.description ? (
                          <Typography
                            sx={{
                              mt: 0.45,
                              fontSize: "0.78rem",
                              lineHeight: 1.5,
                              color: bodyColor,
                              fontFamily: TAAS_SANS,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {page.description}
                          </Typography>
                        ) : null}

                        {isLocked ? (
                          <Chip
                            size="small"
                            label={t("card_toc_locked", { defaultValue: "Locked" })}
                            sx={{
                              mt: 0.6,
                              height: 20,
                              fontFamily: TAAS_SANS,
                              fontWeight: 700,
                              fontSize: "0.62rem",
                              bgcolor: chipBg,
                              color: chipColor,
                              border: surfaceBorder,
                              "& .MuiChip-label": { px: 0.85 },
                            }}
                          />
                        ) : null}
                      </Box>

                      <ChevronRightRoundedIcon
                        sx={{
                          color: isLocked ? mutedColor : accentColor,
                          fontSize: 20,
                          mt: 0.35,
                          flexShrink: 0,
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </Stack>
            </Box>
          </Box>
        ) : null}
      </Stack>
    </Box>
  );
}

const normalizeTocPageTypeKey = (type?: string) =>
  String(type ?? "")
    .replace(/[\s_-]+/g, "")
    .toLowerCase();

const isDemoCardTocContext = (cardData?: any) => {
  const template = String(cardData?.template ?? "")
    .trim()
    .toLowerCase();
  if (template.includes("demo card")) return true;
  const typeKey = normalizeTocPageTypeKey(
    String(cardData?.type ?? cardData?.pages?.[0]?.type ?? "")
  );
  return typeKey === "democard";
};

const isSeasonCardTocContext = (cardData?: any) => {
  const template = String(cardData?.template ?? "")
    .trim()
    .toLowerCase();
  if (template.includes("season card")) return true;
  const typeKey = normalizeTocPageTypeKey(
    String(cardData?.type ?? cardData?.pages?.[0]?.type ?? "")
  );
  return typeKey === "seasoncard" || typeKey.startsWith("seasoncard");
};

const getExpSessionMeta = (exp: Record<string, unknown>): string | undefined => {
  const parts = [exp.session_type, exp.status].filter(Boolean).map(String);
  return parts.length > 0 ? parts.join(" · ") : undefined;
};

const buildExpTocItem = (
  exp: Record<string, unknown>,
  pageIndex: number,
  locked: boolean,
  t: (key: string, options?: { defaultValue?: string; count?: number }) => string
): CardTocPageItem => {
  const title =
    String(exp.title || exp.theme_title || exp.name || "").trim() ||
    formatDeckCardTypeLabel("demo_card_exps") ||
    t("card_toc_page_number", {
      defaultValue: "Page {{count}}",
      count: pageIndex,
    });
  const description = stripDeckCardHtml(
    String(exp.description || exp.summary || exp.caption || "")
  );
  const releaseLabel = getTocExpReleaseLabel(
    {
      release_date: exp.release_date != null ? String(exp.release_date) : undefined,
      start_time: exp.start_time != null ? String(exp.start_time) : undefined,
      title: typeof exp.title === "string" ? exp.title : undefined,
      theme_title: typeof exp.theme_title === "string" ? exp.theme_title : undefined,
      status: typeof exp.status === "string" ? exp.status : undefined,
      session_type: typeof exp.session_type === "string" ? exp.session_type : undefined,
    },
    t
  );

  return {
    pageIndex,
    title,
    description: description || undefined,
    locked,
    releaseLabel,
    sessionMeta: getExpSessionMeta(exp),
  };
};

const getPageContentTitle = (content: Record<string, unknown>): string => {
  const candidates = [
    content.title,
    content.songTitle,
    content.song_title,
    content.theme_title,
    content.name,
  ];
  for (const value of candidates) {
    const text = String(value ?? "").trim();
    if (text) return text;
  }
  return "";
};

/** Friendly TOC label when the page has no content title. */
const getTocTitleForPageType = (
  typeKey: string,
  t: (key: string, options?: { defaultValue?: string; count?: number }) => string
): string => {
  if (typeKey === "audio") {
    return t("card_toc_audio", { defaultValue: "Audio" });
  }
  if (typeKey === "endingimage") {
    return t("card_toc_ending_image", { defaultValue: "Ending Image" });
  }
  // Single-card ending cover is injected as cover_image with no title.
  if (typeKey === "coverimage") {
    return t("card_toc_ending_image", { defaultValue: "Ending Image" });
  }
  if (typeKey === "image") {
    return t("card_toc_image", { defaultValue: "Image" });
  }
  if (typeKey === "video") {
    return t("card_toc_video", { defaultValue: "Video" });
  }
  if (typeKey === "text") {
    return t("card_toc_text", { defaultValue: "Text" });
  }
  if (typeKey === "youtube") {
    return t("card_toc_youtube", { defaultValue: "YouTube" });
  }
  if (typeKey === "postcard" || typeKey === "post_card") {
    return t("card_toc_postcard", { defaultValue: "Postcard" });
  }
  if (typeKey === "news") {
    return t("card_toc_news", { defaultValue: "News" });
  }
  if (typeKey === "about") {
    return t("card_toc_about", { defaultValue: "About" });
  }
  if (typeKey === "bonus") {
    return t("card_toc_bonus", { defaultValue: "Bonus" });
  }
  if (typeKey === "instrument") {
    return t("card_toc_instrument", { defaultValue: "Instrument" });
  }
  if (typeKey === "cardtoc") {
    return t("card_table_of_contents", { defaultValue: "Table of contents" });
  }
  return formatDeckCardTypeLabel(typeKey) || t("card_toc_page", { defaultValue: "Page" });
};

export function buildCardTocPages(options: {
  allPages: Array<{ type?: string; content?: any }>;
  cardData?: any;
  maxNavigablePage: number;
  includeCoverPage?: boolean;
  t: (key: string, options?: { defaultValue?: string; count?: number }) => string;
}): CardTocPageItem[] {
  const { allPages, cardData, maxNavigablePage, includeCoverPage = false, t } = options;
  const coverOffset = includeCoverPage ? 1 : 0;
  const coverTitle = String("Card Description");
  const coverDescription = stripDeckCardHtml(
    String(cardData?.description || cardData?.content?.description || "")
  );

  const cover: CardTocPageItem = {
    pageIndex: 0,
    title: coverTitle,
    description: coverDescription || undefined,
    locked: 0 > maxNavigablePage,
  };

  const contentPages: CardTocPageItem[] = allPages.map((page, index) => {
    const pageIndex = index + coverOffset;
    const content = (page?.content ?? {}) as Record<string, unknown>;
    const typeKey = normalizeTocPageTypeKey(page?.type);
    const locked = pageIndex > maxNavigablePage;

    // Talent "add experience" pages copy the whole card into content, so
    // content.title becomes "username's My Cards" — use a clear TOC label instead.
    const isExpCreationPage =
      typeKey.endsWith("expcreation") ||
      typeKey === "democardnormalexps" ||
      typeKey === "seasoncardnormalexps";

    if (isExpCreationPage) {
      return {
        pageIndex,
        title: t("card_toc_add_more_page", { defaultValue: "Add more page" }),
        locked,
      };
    }

    if (typeKey === "cardtoc") {
      return {
        pageIndex,
        title: t("card_table_of_contents", { defaultValue: "Table of contents" }),
        locked,
      };
    }

    const contentTitle = getPageContentTitle(content);
    const title = contentTitle || getTocTitleForPageType(typeKey, t);
    const description = stripDeckCardHtml(
      String(content?.description || content?.summary || content?.caption || "")
    );

    if (typeKey === "democardexps") {
      return buildExpTocItem(content, pageIndex, locked, t);
    }

    if (typeKey === "seasoncardexps") {
      return buildExpTocItem(content, pageIndex, locked, t);
    }

    if (typeKey === "democardexplist") {
      const episodes = Array.isArray(content?.allEpisodes) ? content.allEpisodes : [];
      return {
        pageIndex,
        title:
          contentTitle ||
          formatDeckCardTypeLabel("demo_card_exp_list") ||
          t("card_toc_episode_list", { defaultValue: "Episodes" }),
        description:
          description ||
          (episodes.length > 0
            ? t("card_toc_overview_body", {
                defaultValue: "{{count}} pages",
                count: episodes.length,
              })
            : undefined),
        locked,
      };
    }

    if (typeKey === "seasoncardexplist") {
      const series = Array.isArray(content?.allEpisodes) ? content.allEpisodes : [];
      return {
        pageIndex,
        title:
          contentTitle ||
          formatDeckCardTypeLabel("season_card_exp_list") ||
          t("card_toc_series_list", { defaultValue: "Series" }),
        description:
          description ||
          (series.length > 0
            ? t("card_toc_overview_body", {
                defaultValue: "{{count}} pages",
                count: series.length,
              })
            : undefined),
        locked,
      };
    }

    return {
      pageIndex,
      title,
      description: description || undefined,
      locked,
      releaseLabel: formatTocReleaseLabel(
        (content?.release_date as string | null | undefined) ||
          (content?.start_time as string | null | undefined) ||
          null,
        t
      ),
    };
  });

  // Unpurchased: blend API table_of_contents into the TOC as locked rows.
  // Keeps real book pages, then adds any TOC titles that are not already listed
  // (needed for single cards that already have pages in the book).
  const isPurchased = cardData?.purchase != null;
  if (!isPurchased) {
    const apiToc = Array.isArray(cardData?.table_of_contents)
      ? (cardData.table_of_contents as Array<{
          type?: string;
          title?: string;
          description?: string;
        }>)
      : [];

    if (apiToc.length > 0) {
      const existingTitles = new Set(
        contentPages
          .map((page) =>
            String(page.title ?? "")
              .trim()
              .toLowerCase()
          )
          .filter(Boolean)
      );

      let nextIndex = contentPages.length + coverOffset;
      for (const item of apiToc) {
        const typeKey = normalizeTocPageTypeKey(item?.type);
        const title =
          String(item?.title ?? "").trim() ||
          getTocTitleForPageType(typeKey, t) ||
          t("card_toc_page", { defaultValue: "Page" });
        const titleKey = title.toLowerCase();
        // Skip duplicates already shown from real book pages.
        if (existingTitles.has(titleKey)) continue;

        const description = stripDeckCardHtml(String(item?.description ?? ""));
        contentPages.push({
          pageIndex: nextIndex,
          title,
          description: description || undefined,
          locked: true,
        });
        existingTitles.add(titleKey);
        nextIndex += 1;
      }
    }
  }

  // Unpurchased demo: episode list + episodes stay in TOC (locked), not as book pages.
  const hasDemoEpisodePages = allPages.some((page) => {
    const key = normalizeTocPageTypeKey(page?.type);
    return key === "democardexps" || key === "democardexplist";
  });
  if (isDemoCardTocContext(cardData) && !isPurchased && !hasDemoEpisodePages) {
    const allExps = findDemoCardEmbeddedExps(cardData);
    if (allExps.length > 0) {
      let nextIndex = contentPages.length + coverOffset;
      contentPages.push({
        pageIndex: nextIndex,
        title:
          formatDeckCardTypeLabel("demo_card_exp_list") ||
          t("card_toc_episode_list", { defaultValue: "Episodes" }),
        description: t("card_toc_overview_body", {
          defaultValue: "{{count}} pages",
          count: allExps.length,
        }),
        locked: true,
      });
      nextIndex += 1;

      for (const exp of allExps) {
        contentPages.push(buildExpTocItem(exp, nextIndex, true, t));
        nextIndex += 1;
      }
    }
  }

  // Unpurchased season: series list + series stay in TOC (locked), same as demo.
  const hasSeasonExpPages = allPages.some((page) => {
    const key = normalizeTocPageTypeKey(page?.type);
    return key === "seasoncardexps" || key === "seasoncardexplist";
  });
  if (isSeasonCardTocContext(cardData) && !isPurchased && !hasSeasonExpPages) {
    const allExps = findSeasonCardEmbeddedExps(cardData);
    if (allExps.length > 0) {
      let nextIndex = contentPages.length + coverOffset;
      contentPages.push({
        pageIndex: nextIndex,
        title:
          formatDeckCardTypeLabel("season_card_exp_list") ||
          t("card_toc_series_list", { defaultValue: "Series" }),
        description: t("card_toc_overview_body", {
          defaultValue: "{{count}} pages",
          count: allExps.length,
        }),
        locked: true,
      });
      nextIndex += 1;

      for (const exp of allExps) {
        contentPages.push(buildExpTocItem(exp, nextIndex, true, t));
        nextIndex += 1;
      }
    }
  }

  return includeCoverPage ? [cover, ...contentPages] : contentPages;
}

// part of code in CardPreviewModal.tsx
{
  /* Page Indicator — TOC on dots disabled for now
            onClick={() => setTocDrawerOpen(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setTocDrawerOpen(true);
              }
            }}
            */
}
// <Box
//   aria-hidden
//   sx={{
//     position: "absolute",
//     bottom: {
//       xs: "max(8px, calc(8px + env(safe-area-inset-bottom, 0px)))",
//       sm: "max(20px, calc(20px + env(safe-area-inset-bottom, 0px)))",
//     },
//     left: "50%",
//     transform: "translateX(-50%)",
//     display: "flex",
//     alignItems: "center",
//     gap: { xs: 1, sm: 1.5 },
//     zIndex: 15,
//     px: { xs: 1.5, sm: 2 },
//     py: { xs: 0.75, sm: 1 },
//     borderRadius: { xs: 4, sm: 6 },
//     background: darkMode ? "rgba(0,0,0,0.4)" : "rgba(255,255,255,0.7)",
//     backdropFilter: "blur(12px)",
//     border: darkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.08)",
//     boxShadow: darkMode ? "0 4px 16px rgba(0,0,0,0.3)" : "0 4px 16px rgba(0,0,0,0.1)",
//     cursor: "default",
//     "&:hover": {
//       background: darkMode ? "rgba(0,0,0,0.5)" : "rgba(255,255,255,0.85)",
//     },
//   }}
// >
//   {Array.from({ length: totalPages }).map((_, index) => (
//     <Box
//       key={index}
//       sx={{
//         width: currentPage === index ? { xs: 24, sm: 32 } : { xs: 6, sm: 8 },
//         height: { xs: 6, sm: 8 },
//         borderRadius: { xs: 3, sm: 4 },
//         background:
//           currentPage === index
//             ? darkMode
//               ? "linear-gradient(135deg, rgba(139,92,246,0.9), rgba(99,102,241,0.9))"
//               : "linear-gradient(135deg, rgba(139,92,246,0.8), rgba(99,102,241,0.8))"
//             : index > maxNavigablePage
//               ? darkMode
//                 ? "rgba(255,255,255,0.12)"
//                 : "rgba(0,0,0,0.12)"
//               : darkMode
//                 ? "rgba(255,255,255,0.25)"
//                 : "rgba(0,0,0,0.25)",
//         transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
//         boxShadow:
//           currentPage === index
//             ? darkMode
//               ? "0 2px 8px rgba(139,92,246,0.4)"
//               : "0 2px 8px rgba(139,92,246,0.3)"
//             : "none",
//         minWidth: { xs: 6, sm: 8 },
//         minHeight: { xs: 6, sm: 8 },
//         pointerEvents: "none",
//       }}
//     />
//   ))}
// </Box>

{
  /* Card TOC bottom drawer — re-enable later
            <Box
              aria-hidden={!tocDrawerOpen}
              onClick={() => setTocDrawerOpen(false)}
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 30,
                bgcolor: darkMode ? "rgba(0,0,0,0.45)" : "rgba(15,23,42,0.28)",
                opacity: tocDrawerOpen ? 1 : 0,
                pointerEvents: tocDrawerOpen ? "auto" : "none",
                transition: "opacity 0.28s ease",
              }}
            />
            <Box
              data-no-card-tap
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 31,
                height: { xs: "68%", sm: "58%" },
                maxHeight: { xs: 520, sm: 480 },
                transform: tocDrawerOpen ? "translateY(0)" : "translateY(105%)",
                transition: "transform 0.4s cubic-bezier(0.22, 1, 0.36, 1)",
                borderTopLeftRadius: "20px",
                borderTopRightRadius: "20px",
                bgcolor: darkMode ? "var(--ink-950)" : "var(--primary-100-light)",
                borderTop: darkMode
                  ? "1px solid color-mix(in srgb, var(--primary-600) 35%, transparent)"
                  : "1px solid var(--primary-300)",
                boxShadow: "0 -12px 40px color-mix(in srgb, var(--neutralBlack) 45%, transparent)",
                px: 2.5,
                pt: 1.5,
                display: "flex",
                flexDirection: "column",
                pointerEvents: tocDrawerOpen ? "auto" : "none",
              }}
              onClick={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onTouchMove={(e) => e.stopPropagation()}
              onTouchEnd={(e) => e.stopPropagation()}
            >
              <Box
                onClick={() => setTocDrawerOpen(false)}
                sx={{
                  width: 42,
                  height: 4,
                  borderRadius: "999px",
                  bgcolor: darkMode
                    ? "color-mix(in srgb, var(--neutralWhite) 28%, transparent)"
                    : "var(--primary-400)",
                  mx: "auto",
                  mb: 1.5,
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              />
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardTocDrawer
                  cardTitle={String("Card Description")}
                  cardDescription={String(
                    cardData?.description || cardData?.content?.description || ""
                  )}
                  pageCount={totalPages}
                  pages={tocPages}
                  currentPage={currentPage}
                  onSelectPage={handleTocSelectPage}
                />
              </Box>
            </Box>
            */
}
